<template>
  <div id="fiscal-config-modal-app">
    <modal
      elem-id="modal_fiscal_config"
      size="medium"
      :close-label="t('Fechar')"
      :save-label="t('Salvar')"
      :save-disabled-fn="() => !formRef?.canSave"
      :save-fn="handleSave"
      :title="modalTitle"
      :show="isVisible"
      @close="closeModal"
      @dismiss="closeModal">
      <template v-if="isVisible">
        <fiscal-config-form
          ref="formRef"
          :franchise-id="franchiseId"
          @save-success="handleSaveSuccess" />
      </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import FiscalConfigForm from './FiscalConfigForm.vue';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';

interface FiscalConfigFormInstance {
  canSave: boolean;
  loadForFranchise: (id: number) => Promise<void>;
  saveFiscalConfig: () => Promise<void>;
  resetForm: () => void;
}

const { t } = useI18n();

const emit = defineEmits<{
  close: [];
}>();

const isVisible = ref<boolean>(false);
const franchiseId = ref<number>(0);
const formRef = ref<FiscalConfigFormInstance | null>(null);

const modalTitle = computed<string>(() => t('Configuração Fiscal'));

const openForFranchise = async (id: number): Promise<void> => {
  log('FiscalConfigModal', 'openForFranchise', 'Opening modal', id);
  franchiseId.value = id;
  isVisible.value = true;
  await nextTick();
  if (formRef.value) {
    await formRef.value.loadForFranchise(id);
  }
};

const closeModal = (): void => {
  log('FiscalConfigModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  franchiseId.value = 0;
  if (formRef.value) {
    formRef.value.resetForm();
  }
  emit('close');
};

const handleSave = async (): Promise<void> => {
  log('FiscalConfigModal', 'handleSave', 'Delegating save to form');
  if (formRef.value) {
    await formRef.value.saveFiscalConfig();
  }
};

const handleSaveSuccess = (): void => {
  log('FiscalConfigModal', 'handleSaveSuccess', 'Save success, closing modal');
  eventBus.emit('fiscal-config.updated', { franchiseId: franchiseId.value });
  closeModal();
};

interface ModalOpenPayload {
  franchiseId?: number;
}

const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('FiscalConfigModal', 'handleModalOpen', 'Modal open event', data);
  if (data?.franchiseId) {
    await openForFranchise(Number(data.franchiseId));
  }
};

onMounted(() => {
  log('FiscalConfigModal', 'mounted', 'Mounted');
  eventBus.on('modal.fiscal-config.open', handleModalOpen);
  eventBus.on('modal.fiscal-config.close', closeModal);
});

onBeforeUnmount(() => {
  eventBus.off('modal.fiscal-config.open', handleModalOpen);
  eventBus.off('modal.fiscal-config.close', closeModal);
});

defineExpose({
  openForFranchise,
  closeModal,
});
</script>
